import { Box, Flex, Stack, Table, Tbody, Td, Text, Tfoot, Th, Thead, Tr, useBreakpointValue } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { AwardLetterEvaluationDto, EventValueDto } from "../../../autogen/bff-api";
import { priceModelType, usePriceModelDescription } from "../../sourcing-events/common/priceModel";
import { comparedStageLabel } from "./comparedStage";

const points = (value?: number | null) => (value == null ? "–" : value.toFixed(2));

const percent = (value?: number | null) => (value == null ? "–" : `${Number(value.toFixed(2))} %`);

const money = (value?: EventValueDto | null) =>
  value?.value == null ? null : `${Number(value.value).toLocaleString("nb-NO")} ${value.currency}`;

export const AwardLetterScoreMatrix = ({
  evaluation,
  criterionLabels,
  recipientIsWinner,
  heading,
}: {
  evaluation: AwardLetterEvaluationDto;
  criterionLabels: Record<string, string>;
  recipientIsWinner: boolean;
  heading: string;
}) => {
  const { t } = useTranslation();
  const describePriceModel = usePriceModelDescription();
  const onPhone = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;
  const criteria = evaluation.criteria ?? [];
  const scores = evaluation.scores ?? {};
  const supplierPrice = money(evaluation.supplierPrice);
  const winnerPrice = recipientIsWinner ? null : money(evaluation.winnerPrice);
  const showsWinner = !recipientIsWinner;
  const comparedRound = showsWinner ? evaluation.comparedRoundNumber : null;
  const comparedStage = comparedStageLabel(t, comparedRound);
  if (criteria.length === 0 && !supplierPrice && !winnerPrice) return null;

  const labelOf = (criterion: (typeof criteria)[number]) =>
    criterionLabels[criterion.id] ?? (criterion.name?.trim() || (criterion.isPrice ? t("Price") : t("Criterion")));
  const rankLine =
    evaluation.supplierRank != null && evaluation.bidderCount != null
      ? t("You were ranked number {{rank}} of {{count}} evaluated bids.", {
          rank: evaluation.supplierRank,
          count: evaluation.bidderCount,
        })
      : null;
  const yourScoreLabel = recipientIsWinner ? t("Score") : t("Your score");
  const winnerScoreLabel = comparedStage
    ? t("Winner's score in {{stage}}", { stage: comparedStage })
    : t("Winner's score");
  const winnerWeightedLabel = comparedStage
    ? t("Winner's weighted score in {{stage}}", { stage: comparedStage })
    : t("Winner's weighted score");
  const withheldLine = (mb?: string) =>
    showsWinner && evaluation.winnerPriceWithheld ? (
      <Text fontSize="sm" color="smMuted" mb={mb}>
        {criteria.some((criterion) => criterion.isPrice)
          ? t("The winner's price and total score are not given, because this competition does not show the other bidders.")
          : t("The winner's price is not given, because this competition does not show the other bidders.")}
      </Text>
    ) : null;
  const winnerTotalLabel = comparedStage
    ? t("Winner's total score in {{stage}}", { stage: comparedStage })
    : t("Winner's total score");

  return (
    <Stack spacing="4" mb="5">
      {criteria.length > 0 && (
        <Box overflowX="auto">
          <Text fontWeight="medium" color="smText" mb="2">
            {heading}
          </Text>
          {comparedStage && (
            <Text fontSize="sm" color="smMuted" mb="2">
              {t(
                "You were assessed in {{stage}}, so the winner's figures below are from the same stage. The contract was awarded on the winner's final tender after a later round.",
                { stage: comparedStage },
              )}
            </Text>
          )}
          {rankLine && (
            <Text fontSize="sm" color="smText" mb="2">
              {rankLine}
            </Text>
          )}
          {withheldLine("2")}
          {onPhone ? (
            <Stack spacing="3">
              {criteria.map((criterion) => {
                const score = scores[criterion.id];
                return (
                  <Box key={criterion.id} borderBottom="1px solid" borderColor="smBorder" pb="2">
                    <Flex justify="space-between" gap="2" mb="1">
                      <Text fontSize="sm" fontWeight="medium" color="smText">
                        {labelOf(criterion)}
                      </Text>
                      <Text fontSize="sm" color="smMuted" fontFamily="mono" whiteSpace="nowrap">
                        {percent(criterion.weightPercent)}
                      </Text>
                    </Flex>
                    <ScoreLine label={yourScoreLabel} value={points(score?.supplierPoints)} />
                    <ScoreLine label={t("Weighted score")} value={points(score?.supplierWeightedPoints)} />
                    {showsWinner && <ScoreLine label={winnerScoreLabel} value={points(score?.winnerPoints)} />}
                    {showsWinner && (
                      <ScoreLine label={winnerWeightedLabel} value={points(score?.winnerWeightedPoints)} />
                    )}
                  </Box>
                );
              })}
              <Box>
                <ScoreLine label={t("Total score")} value={points(evaluation.supplierTotal)} isTotal />
                {showsWinner && <ScoreLine label={winnerTotalLabel} value={points(evaluation.winnerTotal)} isTotal />}
              </Box>
            </Stack>
          ) : (
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>{t("Criterion")}</Th>
                <Th isNumeric>{t("Weight")}</Th>
                <Th isNumeric>{yourScoreLabel}</Th>
                <Th isNumeric>{t("Weighted score")}</Th>
                {showsWinner && <Th isNumeric>{winnerScoreLabel}</Th>}
                {showsWinner && <Th isNumeric>{winnerWeightedLabel}</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {criteria.map((criterion) => {
                const score = scores[criterion.id];
                return (
                  <Tr key={criterion.id}>
                    <Td>{labelOf(criterion)}</Td>
                    <Td isNumeric fontFamily="mono" whiteSpace="nowrap">
                      {percent(criterion.weightPercent)}
                    </Td>
                    <Td isNumeric fontFamily="mono">
                      {points(score?.supplierPoints)}
                    </Td>
                    <Td isNumeric fontFamily="mono">
                      {points(score?.supplierWeightedPoints)}
                    </Td>
                    {showsWinner && (
                      <Td isNumeric fontFamily="mono">
                        {points(score?.winnerPoints)}
                      </Td>
                    )}
                    {showsWinner && (
                      <Td isNumeric fontFamily="mono">
                        {points(score?.winnerWeightedPoints)}
                      </Td>
                    )}
                  </Tr>
                );
              })}
            </Tbody>
            <Tfoot>
              <Tr>
                <Td fontWeight="semibold">{t("Total score")}</Td>
                <Td />
                <Td />
                <Td isNumeric fontFamily="mono" fontWeight="semibold">
                  {points(evaluation.supplierTotal)}
                </Td>
                {showsWinner && <Td />}
                {showsWinner && (
                  <Td isNumeric fontFamily="mono" fontWeight="semibold">
                    {points(evaluation.winnerTotal)}
                  </Td>
                )}
              </Tr>
            </Tfoot>
          </Table>
          )}
        </Box>
      )}

      {(supplierPrice || winnerPrice) && (
        <Box>
          <Text fontWeight="medium" color="smText" mb="1">
            {t("Prices")}
          </Text>
          <Stack spacing="1" fontSize="sm" color="smText">
            {supplierPrice && (
              <Text>
                {t("Your bid sum")}: {supplierPrice}
              </Text>
            )}
            {winnerPrice && (
              <Text>
                {comparedStage
                  ? t("The winner's bid sum in {{stage}}", { stage: comparedStage })
                  : t("The winning bid sum")}
                : {winnerPrice}
              </Text>
            )}
            {criteria.length === 0 && withheldLine()}
            {evaluation.priceModel && (
              <Text color="smMuted">
                {describePriceModel(
                  priceModelType(evaluation.priceModel),
                  evaluation.priceModel.hybrid?.breakpointPercent,
                )}
              </Text>
            )}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

const ScoreLine = ({ label, value, isTotal = false }: { label: string; value: string; isTotal?: boolean }) => (
  <Flex justify="space-between" gap="2" fontSize="sm" fontWeight={isTotal ? "semibold" : "normal"} color="smText">
    <Text>{label}</Text>
    <Text fontFamily="mono">{value}</Text>
  </Flex>
);
